import React, { useRef, useEffect, useState } from 'react';

const useDeviceDetection = () => {
  const [deviceInfo, setDeviceInfo] = useState({
    isMobile: false,
    isTablet: false,
    deviceType: 'desktop',
    orientation: 'landscape',
    hasTouch: false
  });

  useEffect(() => {
    const detectDevice = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      const isTabletUA = /ipad|android(?!.*mobile)|tablet/i.test(userAgent);
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const orientation = width > height ? 'landscape' : 'portrait';
      
      let isMobile = false;
      let isTablet = false;
      
      if (isMobileUA && !isTabletUA) {
        isMobile = true;
      } else if (isTabletUA || (hasTouch && width >= 768 && width <= 1024)) {
        isTablet = true;
      } else if (hasTouch && width < 768) {
        isMobile = true;
      }
      
      setDeviceInfo({ 
        isMobile, 
        isTablet, 
        deviceType: isMobile ? 'mobile' : (isTablet ? 'tablet' : 'desktop'), 
        orientation,
        hasTouch 
      });
    };

    detectDevice();
    window.addEventListener('resize', detectDevice);
    window.addEventListener('orientationchange', detectDevice);
    return () => {
      window.removeEventListener('resize', detectDevice);
      window.removeEventListener('orientationchange', detectDevice);
    };
  }, []);

  return deviceInfo;
};

const SignatureField = ({ value, onChange, label, required = false }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);

  const { hasTouch } = useDeviceDetection();
  const shouldUseMobileModal = hasTouch;

  useEffect(() => {
    if (!shouldUseMobileModal) {
      setTimeout(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        const ctx = canvas.getContext('2d');
        
        // Canvas-Größe setzen
        canvas.width = canvas.offsetWidth;
        canvas.height = 150;
        
        // Canvas-Stil setzen
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        // Hintergrund weiß machen
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Wenn bereits eine Unterschrift vorhanden ist, lade sie
        if (value) {
          const img = new Image();
          img.onload = () => {
            ctx.drawImage(img, 0, 0);
            setHasSignature(true);
          };
          img.src = value;
        }
        
        // Touch Events für Desktop hinzufügen
        const handleTouchStart = (e) => {
          e.preventDefault();
          startDrawing(e);
        };
        
        const handleTouchMove = (e) => {
          e.preventDefault();
          draw(e);
        };
        
        const handleTouchEnd = (e) => {
          e.preventDefault();
          stopDrawing();
        };
        
        canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
        canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
        canvas.addEventListener('touchend', handleTouchEnd, { passive: false });
        
        return () => {
          canvas.removeEventListener('touchstart', handleTouchStart);
          canvas.removeEventListener('touchmove', handleTouchMove);
          canvas.removeEventListener('touchend', handleTouchEnd);
        };
      }, 100);
    }
  }, [value, shouldUseMobileModal]);

  const getEventPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    const pos = getEventPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const pos = getEventPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      const dataURL = canvas.toDataURL('image/png');
      onChange(dataURL);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onChange('');
  };

  const openMobileModal = () => setShowMobileModal(true);
  const closeMobileModal = () => setShowMobileModal(false);
  const saveMobileSignature = (signatureData) => {
    onChange(signatureData);
    setHasSignature(true);
    closeMobileModal();
  };

  if (shouldUseMobileModal) {
    return (
      <div className="signature-field">
        <label className={`form-label ${required ? 'required' : ''}`}>
          {label}
        </label>
        
        {hasSignature && (
          <div style={{ border: '2px solid #ddd', borderRadius: '8px', padding: '12px', background: '#f9f9f9', marginBottom: '12px' }}>
            <img src={value} alt="Unterschrift" style={{ maxWidth: '100%', maxHeight: '60px', background: 'white' }} />
            <div style={{ color: '#27ae60', fontSize: '14px', marginTop: '8px' }}>✓ Unterschrift erfasst</div>
          </div>
        )}
        
        <button 
          type="button" 
          onClick={openMobileModal}
          style={{
            width: '100%',
            padding: '12px',
            background: '#3498db',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            cursor: 'pointer'
          }}
        >
          {hasSignature ? 'Unterschrift ändern' : 'Unterschrift hinzufügen'}
        </button>
        
        {hasSignature && (
          <button 
            type="button" 
            onClick={() => { onChange(''); setHasSignature(false); }}
            style={{
              width: '100%',
              padding: '8px',
              background: '#e74c3c',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              marginTop: '8px',
              cursor: 'pointer'
            }}
          >
            Unterschrift löschen
          </button>
        )}
  
        {/* Mobile Modal */}
        {showMobileModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}>
            <MobileSignatureCanvas 
              onSave={saveMobileSignature}
              onClose={closeMobileModal}
              initialSignature={value}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="signature-field">
      <label className={`form-label ${required ? 'required' : ''}`}>
        {label}
      </label>
      <div className="signature-container">
        <canvas
          ref={canvasRef}
          className="signature-canvas"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
        />
        <div className="signature-instructions">
          {hasSignature ? 
            "Unterschrift erfasst" : 
            "Bitte unterschreiben Sie hier"
          }
        </div>
        {hasSignature && (
          <button 
            type="button" 
            className="clear-signature-button"
            onClick={clearSignature}
          >
            Unterschrift löschen
          </button>
        )}
      </div>
    </div>
  );

};

const MobileSignatureCanvas = ({ onSave, onClose, initialSignature }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [canvasInitialized, setCanvasInitialized] = useState(false);

  useEffect(() => {
    // Scroll sperren
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Canvas initialisieren
    const initCanvas = () => {
      const ctx = canvas.getContext('2d');
      
      // Canvas physische Größe setzen
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * window.devicePixelRatio;
      canvas.height = 200 * window.devicePixelRatio;
      
      // CSS-Größe beibehalten
      canvas.style.width = rect.width + 'px';
      canvas.style.height = '200px';
      
      // Context für High-DPI skalieren
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      
      // Canvas-Stil setzen
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // Hintergrund nur EINMAL beim Initialisieren setzen
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, rect.width, 200);
      
      // Bestehende Unterschrift laden
      if (initialSignature) {
        const img = new Image();
        img.onload = () => { 
          ctx.drawImage(img, 0, 0, rect.width, 200); 
          setHasSignature(true); 
        };
        img.src = initialSignature;
      }
      
      setCanvasInitialized(true);
    };

    // Canvas initialisieren mit Verzögerung
    const timeoutId = setTimeout(initCanvas, 100);
    
    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      setCanvasInitialized(false);
    };
  }, []); // Nur beim Mount ausführen

  useEffect(() => {
    if (!canvasInitialized) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Touch Events
    const getEventPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches ? e.touches[0] : e;
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };
    };

    const handleTouchStart = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      setIsDrawing(true);
      const pos = getEventPos(e);
      const ctx = canvas.getContext('2d');
      
      // Neuen Pfad beginnen für eine neue Linie
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      
      // Ein kleiner Punkt wenn nur getippt wird
      ctx.lineTo(pos.x + 0.1, pos.y + 0.1);
      ctx.stroke();
      setHasSignature(true);
    };
    
    const handleTouchMove = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (!isDrawing) return;
      
      const pos = getEventPos(e);
      const ctx = canvas.getContext('2d');
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      
      setHasSignature(true);
    };
    
    const handleTouchEnd = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (isDrawing) {
        setIsDrawing(false);
      }
    };

    // Mouse Events (für Desktop Fallback)
    const handleMouseDown = (e) => {
      e.preventDefault();
      setIsDrawing(true);
      const pos = getEventPos(e);
      const ctx = canvas.getContext('2d');
      
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      
      ctx.lineTo(pos.x + 0.1, pos.y + 0.1);
      ctx.stroke();
      setHasSignature(true);
    };

    const handleMouseMove = (e) => {
      e.preventDefault();
      if (!isDrawing) return;
      
      const pos = getEventPos(e);
      const ctx = canvas.getContext('2d');
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      
      setHasSignature(true);
    };

    const handleMouseUp = (e) => {
      e.preventDefault();
      if (isDrawing) {
        setIsDrawing(false);
      }
    };
    
    // Event Listener hinzufügen
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });
    canvas.addEventListener('touchcancel', handleTouchEnd, { passive: false });
    
    // Mouse Events als Fallback
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('mouseleave', handleMouseUp);

    // Cleanup
    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('touchcancel', handleTouchEnd);
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [canvasInitialized, isDrawing]); // Abhängig von canvasInitialized und isDrawing

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Canvas komplett leeren und weiß füllen
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, rect.width, 200);
    
    setHasSignature(false);
  };

  const save = () => {
    if (hasSignature) {
      const dataURL = canvasRef.current.toDataURL('image/png');
      onSave(dataURL);
    }
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      width: '100%',
      maxWidth: '500px',
      padding: '20px',
      maxHeight: '90vh',
      overflow: 'hidden'
    }}>
      <h3 style={{ margin: '0 0 20px 0', textAlign: 'center' }}>Unterschrift</h3>
      
      <div style={{ 
        border: '2px solid #ddd', 
        borderRadius: '8px', 
        marginBottom: '20px',
        touchAction: 'none',
        userSelect: 'none'
      }}>
        <canvas
          ref={canvasRef}
          style={{ 
            width: '100%', 
            height: '200px', 
            display: 'block',
            touchAction: 'none',
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
            userSelect: 'none'
          }}
        />
      </div>
      
      <div style={{ 
        display: 'flex', 
        gap: '10px',
        flexWrap: 'wrap'
      }}>
        <button 
          type="button"
          onClick={clear} 
          style={{ 
            flex: '1', 
            minWidth: '80px',
            padding: '12px', 
            background: '#f5f5f5', 
            color: '#333',
            border: '1px solid #ddd',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Löschen
        </button>
        <button 
          type="button"
          onClick={onClose} 
          style={{ 
            flex: '1', 
            minWidth: '80px',
            padding: '12px', 
            background: '#e74c3c', 
            color: 'white', 
            border: 'none', 
            borderRadius: '6px',
            fontSize: '14px'
          }}
        >
          Abbrechen
        </button>
        <button 
          type="button"
          onClick={save} 
          disabled={!hasSignature} 
          style={{ 
            flex: '1', 
            minWidth: '80px',
            padding: '12px', 
            background: hasSignature ? '#27ae60' : '#bdc3c7', 
            color: 'white', 
            border: 'none', 
            borderRadius: '6px',
            fontSize: '14px'
          }}
        >
          Bestätigen
        </button>
      </div>
    </div>
  );
};

export default SignatureField;